import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL  = 'https://accugen.ai';
const SITE_NAME = 'AccuGen';
const OG_IMAGE  = `${SITE_URL}/og-image.png`;

/**
 * SEO component — place at the top of every page.
 *
 * Props:
 *  title       string  — page title (appended with " | AccuGen")
 *  description string  — meta description (≤160 chars)
 *  canonical   string  — canonical path e.g. "/features"
 *  keywords    string  — comma-separated keywords (optional)
 *  schema      object  — JSON-LD structured data object (optional)
 *  noIndex     bool    — if true, adds noindex,nofollow
 */
export default function SEO({
  title,
  description,
  canonical = '/',
  keywords,
  schema,
  noIndex = false,
}) {
  const fullTitle   = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Where AI Meets Reality`;
  const canonicalUrl = `${SITE_URL}${canonical}`;

  return (
    <Helmet>
      {/* Primary */}
      <title>{fullTitle}</title>
      <meta name="description"  content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noIndex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-image-preview:large" />
      }
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url"         content={canonicalUrl} />
      <meta property="og:image"       content={OG_IMAGE} />
      <meta property="og:type"        content="website" />
      <meta property="og:site_name"   content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={OG_IMAGE} />

      {/* JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}

/* ── Pre-built schemas ─────────────────────────────── */

export const schemas = {

  webSite: () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: 'Enterprise-grade AI quality and security evaluation platform.',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  }),

  softwareApp: () => ({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'AccuGen',
    url: SITE_URL,
    applicationCategory: 'DeveloperApplication',
    operatingSystem: 'Web',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: 'Free to evaluate' },
    description: 'Enterprise AI chatbot evaluation platform with OWASP LLM Top 10 security testing, bias detection, and quality scoring.',
    keywords: 'AI evaluation, LLM security, OWASP LLM Top 10, bias detection',
    featureList: [
      'OWASP LLM Top 10 security testing',
      '122+ security test cases',
      '12-dimension bias detection',
      'LLM-as-Judge quality scoring',
      'CI/CD pipeline integration',
      'PDF compliance reports',
    ],
  }),

  breadcrumb: (items) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  }),

  faqPage: (faqs) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  }),

  howTo: (steps) => ({
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Evaluate AI with AccuGen',
    description: 'Get from configuration to OWASP compliance report in 4 steps.',
    step: steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.title,
      text: s.text,
    })),
  }),

  contactPage: () => ({
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact AccuGen',
    url: `${SITE_URL}/contact`,
    description: 'Schedule an AI evaluation or get in touch with the AccuGen team.',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'accugen@proautomate.ai',
      contactType: 'customer support',
      availableLanguage: 'English',
    },
  }),
};
